<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="face-collection-page">
            <!-- 顶部统计卡片 -->
            <div class="stats-section">
                <div class="stats-grid">
                    <div class="stat-card new-auth">
                        <div class="stat-title">今日新增授权</div>
                        <div class="stat-value">{{ stats.today_authorized }}</div>
                    </div>
                    <div class="stat-card total-outlets">
                        <div class="stat-title">网点总数</div>
                        <div class="stat-value">{{ stats.total_places }}</div>
                    </div>
                    <div class="stat-card total-students">
                        <div class="stat-title">学生总数</div>
                        <div class="stat-value">{{ stats.total_students }}</div>
                    </div>
                    <div class="stat-card not-registered">
                        <div class="stat-title">未录入人脸</div>
                        <div class="stat-value">{{ stats.not_recorded }}</div>
                    </div>
                </div>
            </div>

            <!-- 使用van-tabs组件 -->
            <van-tabs v-model:active="activeTab" @change="onTabChange" class="custom-tabs">
                <!-- 授权采集人脸tab -->
                <van-tab title="授权采集人脸" name="authorize">
                    <!-- 搜索栏 -->
                    <div class="search-section">
                        <div class="search-bar">
                            <van-icon name="search" class="search-icon" @click="handleSearch" />
                            <van-field v-model="searchKeyword" placeholder="搜索学校、班级或学生姓名" class="search-input"
                                @keyup.enter="handleSearch" />
                        </div>
                    </div>

                    <!-- 授权采集人脸内容 -->
                    <div class="students-section">
                        <van-empty v-if="students.length === 0" description="暂无数据" />
                        <div class="students-list" v-else>
                            <div class="student-card" v-for="student in students" :key="student.id">
                                <div class="student-info">
                                    <div class="student-name">{{ student.student_name }}</div>
                                    <div class="student-details">{{ student.place_name }} {{ student.grade_name }} {{
                                        student.class_name }}</div>
                                </div>
                                <div class="student-status">
                                    <van-button v-if="student.state == 'pending'" type="primary" size="small"
                                        class="register-face-btn" @click="registerFace(student)">
                                        录入人脸
                                    </van-button>
                                    <van-button v-if="student.state == 'completed'" type="default" size="small"
                                        class="registered-btn" disabled>
                                        已录入
                                    </van-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </van-tab>

                <!-- 批量采集人脸tab -->
                <van-tab title="批量采集人脸" name="batch">
                    <!-- 筛选条件区域 -->
                    <div class="filter-section">
                        <div class="filter-item">
                            <van-field v-model="selectedSchool.name" readonly placeholder="请选择学校"
                                @click="showSchoolPicker = true" class="filter-field" />
                        </div>
                        <div class="filter-row">
                            <div class="filter-item">
                                <van-field v-model="selectedGrade.name" readonly placeholder="请选择年级"
                                    @click="showGradePicker = true" class="filter-field" />
                            </div>
                            <div class="filter-item">
                                <van-field v-model="selectedClass.name" readonly placeholder="请选择班级"
                                    @click="showClassPicker = true" class="filter-field" />
                            </div>
                        </div>
                    </div>

                    <!-- 批量采集人脸内容 -->
                    <div class="students-section">
                        <van-empty v-if="Object.keys(batchStudents).length === 0" description="暂无数据" />
                        <div class="students-list" v-else>
                            <div class="student-card" v-for="student in batchStudents.student_list" :key="student.id">
                                <div class="student-info">
                                    <div class="student-name">{{ student.name }}</div>
                                    <div class="student-details">{{ batchStudents.school_name }} {{ batchStudents.grade_name }} {{
                                        batchStudents.name }}</div>
                                </div>
                                <div class="student-status">
                                    <van-button v-if="Object.keys(student.face_image_arr || {}).length === 0" type="primary" size="small"
                                        class="register-face-btn" @click="registerFace(student)">
                                        录入人脸
                                    </van-button>
                                    <van-button v-if="Object.keys(student.face_image_arr || {}).length > 0" type="default" size="small"
                                        class="registered-btn" disabled>
                                        已录入
                                    </van-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </van-tab>
            </van-tabs>

            <!-- 学校选择器（支持搜索） -->
            <SearchablePicker v-model:show="showSchoolPicker" title="选择学校" :options="schoolOptions"
                :value="selectedSchool" value-key="id" display-key="name" @confirm="onSchoolConfirm"
                @cancel="showSchoolPicker = false" />

            <!-- 年级选择弹窗 -->
            <van-popup v-model:show="showGradePicker" position="bottom" round>
                <van-picker :columns="gradeOptions" @confirm="onGradeConfirm" @cancel="showGradePicker = false"
                    title="选择年级" :columns-field-names="customFieldName" />
            </van-popup>

            <!-- 班级选择弹窗 -->
            <van-popup v-model:show="showClassPicker" position="bottom" round>
                <van-picker :columns="classOptions" @confirm="onClassConfirm" @cancel="showClassPicker = false"
                    title="选择班级" :columns-field-names="customFieldName" />
            </van-popup>

            <!-- 加载遮罩 -->
            <LoadingOverlay :show="isLoading" />
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { setWxTitle } from '@/utils/wx'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'
import SearchablePicker from '@/components/SearchablePicker.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'

// 加载状态
const loading = ref(true)
const isLoading = ref(false)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 统计数据
const stats = ref({})

// 当前激活的tab
const activeTab = ref('authorize')

// 搜索关键词
const searchKeyword = ref('')

// 学生数据
const students = ref([])
const batchStudents = ref({})

// 筛选条件
const selectedSchool = ref('')
const selectedGrade = ref('')
const selectedClass = ref('')

// 自定义字段名
const customFieldName = {
    text: 'name',
    value: 'id'
}

// 选择器弹窗状态
const showSchoolPicker = ref(false)
const showGradePicker = ref(false)
const showClassPicker = ref(false)

// 选择器选项
const schoolOptions = ref([])
const gradeOptions = ref([])
const classOptions = ref([])

onMounted(() => {
    setWxTitle('人脸采集管理')
    const faceCollectionClass = localStorage.getItem('face_collection_class')
    if (faceCollectionClass) {
        const faceCollectionClassObj = JSON.parse(faceCollectionClass)
        selectedSchool.value = faceCollectionClassObj.selectedSchool
        gradeOptions.value = selectedSchool.value.common_grade_arr || []
        selectedGrade.value = faceCollectionClassObj.selectedGrade
        classOptions.value = selectedGrade.value.class_arr || []
        selectedClass.value = faceCollectionClassObj.selectedClass
        localStorage.removeItem('face_collection_class')
    }
    const faceCollectionSearchKeyword = localStorage.getItem('face_collection_search_keyword')
    if (faceCollectionSearchKeyword) {
        searchKeyword.value = faceCollectionSearchKeyword
        localStorage.removeItem('face_collection_search_keyword')
    }
    initPage()
})

// tab切换事件处理
const onTabChange = (name) => {
    activeTab.value = name
}

// 搜索处理
const handleSearch = () => {
    console.log('搜索关键词:', searchKeyword.value)
    isLoading.value = true
    initPage()
}

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 页面初始化
const initPage = async () => {
    try {
        // 这里可以调用API获取人脸采集数据
        const params = {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            partner_id: parseInt(localStorage.getItem('userId')),
        }
        if (searchKeyword.value) {
            params.search_keyword = searchKeyword.value
        }
        if (selectedClass.value) {
            params.class_id = selectedClass.value.id
        }
        const res = await post('/api/face/collection/management', params)
        if (res.result && res.result.state === 'ok') {
            // 处理统计数据
            stats.value = res.result.statistics || {}
            students.value = res.result.records || []
            batchStudents.value = res.result.class_info || {}
            schoolOptions.value = res.result.place_arr || []
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }

    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
        isLoading.value = false
    }
}

// 录入人脸
const registerFace = (student) => {
    console.log('录入人脸', student)
    if (selectedClass.value) {
        localStorage.setItem('face_collection_class', JSON.stringify({
            selectedSchool: selectedSchool.value,
            selectedGrade: selectedGrade.value,
            selectedClass: selectedClass.value,
        }))
    } else {
        localStorage.removeItem('face_collection_class')
    }
    if (searchKeyword.value) {
        localStorage.setItem('face_collection_search_keyword', searchKeyword.value)
    } else {
        localStorage.removeItem('face_collection_search_keyword')
    }
    var partner_id = student.id
    if (activeTab.value === 'authorize') {
        partner_id = student.student_id
    } else {
        partner_id = student.id
    }
    window.location.href = '/face-recognition?partner_id=' + partner_id + '&model=face_collection'
}

// 学校选择确认
const onSchoolConfirm = (value) => {
    selectedSchool.value = value
    gradeOptions.value = value.common_grade_arr
    showSchoolPicker.value = false
}

// 年级选择确认
const onGradeConfirm = (value) => {
    selectedGrade.value = value
    classOptions.value = value.class_arr
    showGradePicker.value = false
}

// 班级选择确认
const onClassConfirm = (value) => {
    selectedClass.value = value
    showClassPicker.value = false
    isLoading.value = true
    initPage()
}
</script>

<style scoped>
.face-collection-page {
    background: #fff;
    min-height: calc(100vh - 28px);
    padding: 14px;
}

.stats-section {
    margin-bottom: 21px;
}

.stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.stat-card {
    border-radius: 7px;
    padding: 14px;
}

.stat-title {
    font-size: 12px;
    color: #6B7280;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 16px;
    font-weight: 500;
}

.stat-card.new-auth {
    color: var(--van-primary-color);
    background: #FEF2F2;
}

.stat-card.total-outlets {
    color: #2563EB;
    background: #EFF6FF;
}

.stat-card.total-students {
    color: #16A34A;
    background: #F0FDF4;
}

.stat-card.not-registered {
    color: #CA8A04;
    background: #FEFCE8;
}

/* 自定义van-tabs样式 */
.custom-tabs {
    margin-bottom: 21px;
}

.custom-tabs :deep(.van-tabs__wrap) {
    margin-bottom: 16px;
}

.custom-tabs :deep(.van-tabs__nav) {
    padding: 0;
}

/* 筛选条件区域样式 */
.filter-section {
    margin-bottom: 21px;
}

.filter-row {
    display: flex;
    gap: 10px;
    margin-top: 10px;
}

.filter-item {
    flex: 1;
}

.filter-field {
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 5px 10px;
}

.search-section {
    margin-bottom: 21px;
}

.search-bar {
    position: relative;
    border-radius: 8px;
    padding-left: 12px;
    display: flex;
    align-items: center;
    border: 1px solid #E5E7EB;
}

.search-icon {
    color: #999;
    font-size: 16px;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    padding: 5px 10px;
}

.search-input :deep(.van-field__control) {
    border: none;
    background: transparent;
}

/* 学生列表区域样式 */
.students-section {
    flex: 1;
}

.students-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.student-card {
    border-radius: 7px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    padding: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.student-info {
    flex: 1;
}

.student-name {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
}

.student-details {
    font-size: 12px;
    color: #6B7280;
}

.student-status {
    flex-shrink: 0;
}

.register-face-btn {
    border-radius: 7px;
    font-size: 12px;
    height: 28px;
}

.registered-btn {
    border: none;
    background: #F3F4F6;
    color: #4B5563;
    border-radius: 7px;
    font-size: 12px;
    height: 28px;
}
</style>